import { ROUTE_ALIASES } from '@/routing/routesAliases';
import { CHURCH_PRODUCT } from '@/types';

const PRODUCT_ROUTE_MAP: Partial<Record<CHURCH_PRODUCT, string>> = {
  [CHURCH_PRODUCT.MPA]: ROUTE_ALIASES.HOME,
  [CHURCH_PRODUCT.MESSAGES]: ROUTE_ALIASES.MESSAGES_INBOX,
  [CHURCH_PRODUCT.FORMS]: ROUTE_ALIASES.FORMS_PUBLISHED,
  [CHURCH_PRODUCT.MY_PARISH_MINISTRIES]: ROUTE_ALIASES.MINISTRIES,
};

export const ADMIN_PRODUCT_REDIRECT_PRIORITY: readonly CHURCH_PRODUCT[] = [
  CHURCH_PRODUCT.MPA,
  CHURCH_PRODUCT.MESSAGES,
  CHURCH_PRODUCT.FORMS,
  CHURCH_PRODUCT.DISCOVER_MASS,
  CHURCH_PRODUCT.BULLETIN_RESOURCES,
  CHURCH_PRODUCT.LIBRARY_OF_ART,
  CHURCH_PRODUCT.MY_PARISH_FACILITIES,
  CHURCH_PRODUCT.MY_PARISH_MINISTRIES,
  CHURCH_PRODUCT.MY_PARISH_ADORATION,
  CHURCH_PRODUCT.MASS_INTENTIONS,
  CHURCH_PRODUCT.WEBSITES,
];

export type ChurchProductsMap = Record<
  CHURCH_PRODUCT,
  {
    isEnabled: boolean;
    url: string;
  }
>;

export type AdminProductRedirectTarget =
  { kind: 'internal'; path: string } | { kind: 'external'; url: string } | { kind: 'none' };

export const getProductInternalRoute = ({ product }: { product: CHURCH_PRODUCT }): string | null => {
  return PRODUCT_ROUTE_MAP[product] ?? null;
};

export const resolveAdminProductRedirectTarget = ({
  savedProduct,
  churchProducts,
}: {
  savedProduct: CHURCH_PRODUCT | undefined;
  churchProducts: ChurchProductsMap | undefined;
}): AdminProductRedirectTarget => {
  if (!churchProducts) {
    return { kind: 'none' };
  }

  const pickTargetForProduct = ({ product }: { product: CHURCH_PRODUCT }): AdminProductRedirectTarget => {
    const entry = churchProducts[product];
    if (!entry?.isEnabled) {
      return { kind: 'none' };
    }
    const internalPath = getProductInternalRoute({ product });
    if (internalPath) {
      return { kind: 'internal', path: internalPath };
    }
    const trimmedUrl = entry.url?.trim();
    if (trimmedUrl) {
      return { kind: 'external', url: trimmedUrl };
    }
    return { kind: 'none' };
  };

  if (savedProduct) {
    const savedTarget = pickTargetForProduct({ product: savedProduct });
    if (savedTarget.kind !== 'none') {
      return savedTarget;
    }
  }

  for (const product of ADMIN_PRODUCT_REDIRECT_PRIORITY) {
    const target = pickTargetForProduct({ product });
    if (target.kind !== 'none') {
      return target;
    }
  }

  return { kind: 'none' };
};
